import type Uppy from "@uppy/core"
import { type FailedUppyFile, type UploadedUppyFile, type UppyFile } from "@uppy/core"
import { DragDrop } from "@uppy/react"
import cn from "classnames"
import React, { useCallback, useEffect, useState } from "react"
import { type UseFormRegister } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { type InvalidUppyFile } from "@/common/hooks/use_uppy"
import { CheckmarkFill, CloseLarge, ErrorFill, LoadingCircle } from "@/skylticons"

interface UploaderFileProps {
  uppy: Uppy
  file: UploadedUppyFile<any, any> | FailedUppyFile<any, any>
  name?: string
  formRegister?: UseFormRegister<any>
  onSuccess: () => void
  onRemove: () => void
  formError: boolean
}

const UploaderFile: React.FC<UploaderFileProps> = ({
  uppy,
  file,
  name,
  formRegister,
  onSuccess,
  onRemove,
  formError,
}) => {
  const [fileData, setFileData] = useState<any>()

  useEffect(() => {
    const success = (uploaded: UppyFile, response: any) => {
      if (file.id !== uploaded.id) {
        return
      }

      const uploadedFileData = {
        id: response.uploadURL,
        storage: "cache",
        metadata: {
          size: uploaded.size,
          filename: uploaded.name,
          mime_type: uploaded.type,
        },
      }

      setFileData(JSON.stringify(uploadedFileData))

      onSuccess()
    }

    uppy.on("upload-success", success)

    return () => {
      uppy.off("upload-success", success)
    }
  }, [uppy, file, onSuccess])

  const done = !!fileData

  if (formError) {
    return (
      <div className="uploader-item uploader-item-invalid">
        <div className="icon">
          <ErrorFill />
        </div>
        <div className="info">
          <div className="name">{file.name}</div>
        </div>
        <div className="buttons">
          <button
            className="delete"
            onClick={e => {
              e.stopPropagation()
              uppy.removeFile(file.id)
              onRemove()
            }}
            type="button"
          >
            <CloseLarge />
          </button>
        </div>
      </div>
    )
  }

  let icon = <LoadingCircle className="loading" />

  if (done) {
    if (file?.preview) {
      icon = <img src={file.preview} />
    } else {
      icon = <CheckmarkFill />
    }
  }

  const inputName = `${name || "file"}[0]`
  const registerFields = formRegister && formRegister(inputName)

  return (
    <div className={cn("uploader-item", { done, uploading: !done })}>
      <div className="progress" style={{ width: `${file.progress?.percentage || 0}%` }}></div>
      <div data-hj-suppress className="icon">
        {icon}
      </div>
      <div className="info">
        <div className="name data-hj-suppress">{file.name}</div>
      </div>
      <div className="buttons">
        <button
          className="delete"
          onClick={e => {
            e.stopPropagation()
            uppy.removeFile(file.id)
            onRemove && onRemove()
          }}
          type="button"
        >
          <CloseLarge />
        </button>
      </div>
      {fileData && <input type="hidden" value={fileData} name={inputName} {...registerFields} />}
    </div>
  )
}

interface InvalidFileProps {
  file: InvalidUppyFile
  removeFile: (file: InvalidUppyFile) => void
}

const InvalidFile: React.FC<InvalidFileProps> = ({ file, removeFile }) => {
  const deleteFile = useCallback(() => {
    removeFile(file)
  }, [removeFile, file])

  return (
    <>
      <div className="uploader-item uploader-item-invalid">
        <div className="icon">
          <ErrorFill />
        </div>
        <div className="info">
          <div className="name">{file.name}</div>
        </div>
        <div className="buttons">
          <button
            className="delete"
            onClick={e => {
              e.stopPropagation()
              deleteFile()
            }}
            type="button"
          >
            <CloseLarge />
          </button>
        </div>
      </div>
      <div className="uploader-error paragraph-2xs mb-0">{file.error}</div>
    </>
  )
}

interface UploaderProps {
  uppy: Uppy
  name: string
  formRegister: UseFormRegister<any>
  files: (UploadedUppyFile<any, any> | FailedUppyFile<any, any>)[]
  invalidFiles: InvalidUppyFile[]
  maxNumberOfFiles: number
  onSuccess: () => void
  onRemove: () => void
  removeInvalidFile: (file: InvalidUppyFile) => void
  formError?: string
}

const Uploader: React.FC<UploaderProps> = ({
  uppy,
  name,
  formRegister,
  files,
  invalidFiles,
  maxNumberOfFiles,
  onSuccess,
  onRemove,
  removeInvalidFile,
  formError,
}) => {
  const { t } = useTranslation()

  return (
    <>
      {files.length != maxNumberOfFiles && (
        <DragDrop
          uppy={uppy}
          height="96"
          locale={{
            strings: {
              dropHereOr: "%{browse}",
              browse: t("excel_import.drag_upload"),
            },
          }}
        />
      )}
      {invalidFiles.map(file => (
        <InvalidFile key={file.id} file={file} removeFile={removeInvalidFile} />
      ))}
      {files[0] && (
        <UploaderFile
          uppy={uppy}
          file={files[0]}
          name={name}
          formRegister={formRegister}
          onSuccess={onSuccess}
          onRemove={onRemove}
          formError={!!formError}
        />
      )}
      {formError && <div className="paragraph-2xs mb-0 mt-1 text-error">{formError}</div>}
    </>
  )
}

export default Uploader
